import React from "react";
import Link from "gatsby-link";
import styled, { ThemeProvider } from "styled-components";
import { Box, Flex } from "rebass";
import { H4, Button, PrimaryButton, Text } from "components";
import {
  FacebookIcon,
  TwitterIcon,
  YouTubeIcon,
  GooglePlusIcon,
} from "components/Icons";

import invertTheme from "../invertTheme.js";

const Wrapper = styled(Box)`
  width: 100%;
  * {
    &::selection {
      color: ${(props) => props.theme.colors.grey[2]};
      background-color: ${(props) => props.theme.colors.violet};
    }
  }
`;
Wrapper.defaultProps = {
  is: "footer",
  bg: "grey.1",
  mt: "-1px",
};

const Container = styled(Box)`
  max-width: 90em;
`;
Container.defaultProps = {
  is: "section",
  w: "100%",
  mx: "auto",
  px: [4, 5, 6, 8],
  py: [4, 6],
};

const ItemLayout = styled(Flex)`
  @media (max-width: 35em) {
    flex-wrap: wrap;
    > * {
      width: 100%;
      margin-bottom: ${(props) => props.theme.space[6]}px;

      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
  }
  @media (min-width: 35em) and (max-width: 75em) {
    flex-wrap: wrap;
    > * {
      width: ${(props) =>
        "calc(" + (1 / 2) * 100 + "% - " + props.theme.space[6] / 2 + "px)"};
      margin-bottom: ${(props) => props.theme.space[6]}px;

      &:not(:lang(ar)) {
        margin-left: ${(props) => props.theme.space[6]}px;
      }
      &:lang(ar) {
        margin-right: ${(props) => props.theme.space[6]}px;
      }
    }
    > *:nth-child(2n + 1) {
      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
  }
  @media (min-width: 75em) {
    flex-wrap: wrap;
    > * {
      width: ${(props) =>
        "calc(" +
        (1 / 4) * 100 +
        "% - " +
        props.theme.space[6] / (1 / 3 + 1) +
        "px)"};
      margin-bottom: ${(props) => props.theme.space[6]}px;

      &:not(:lang(ar)) {
        margin-left: ${(props) => props.theme.space[6]}px;
      }
      &:lang(ar) {
        margin-right: ${(props) => props.theme.space[6]}px;
      }
    }
    > *:nth-child(4n + 1) {
      &:not(:lang(ar)) {
        margin-left: 0;
      }
      &:lang(ar) {
        margin-right: 0;
      }
    }
  }
`;

const LanguageContainer = styled(Box)`
  display: block;
  cursor: pointer;
  padding-bottom: 8px;
  &:last-child {
    padding-bottom: 0;
  }
  &:hover > span:first-child {
    box-shadow: inset 0 -1px 0 ${(props) => props.theme.colors.grey[0]};
  }
  > span:first-child {
    transition: box-shadow 500ms cubic-bezier(0.23, 1, 0.32, 1);
    will-change: box-shadow;
  }
`;

const LanguageItem = (props) => (
  <LanguageContainer
    lang={props.lang}
    title={props.translation + " (" + props.native + ")"}
    onClick={props.onClick}
    tabIndex={0}
    role="button"
  >
    <Text children={props.translation} is="span" fontSize={1} />
    <Text children={" " + props.native} is="span" color="violet" fontSize={1} />
  </LanguageContainer>
);

const SocialMedia = (props) => (
  <Flex align="center" mt={3}>
    <Box
      is="a"
      href="https://www.facebook.com/BankInformationCenter"
      target="_blank"
      mr={3}
    >
      <FacebookIcon />
    </Box>
    <Box is="a" href="https://twitter.com/BIC_Updates" target="_blank" mr={3}>
      <TwitterIcon />
    </Box>
    <Box
      is="a"
      href="https://plus.google.com/103958583605622125042"
      target="_blank"
      mr={3}
    >
      <GooglePlusIcon />
    </Box>
    <Box
      is="a"
      href="https://www.youtube.com/channel/UCNV3weatySNjywBHOchHfZQ"
      target="_blank"
    >
      <YouTubeIcon />
    </Box>
  </Flex>
);

const Footer = (props) => (
  <React.Fragment>
    <Wrapper role="contentinfo">
      <Container>
        <ItemLayout mt={6}>
          <Box>
            <H4
              children={
                props.windowLang === "en-us"
                  ? "Languages"
                  : props.windowLang === "es-mx"
                  ? "Idiomas"
                  : "لغات"
              }
              mb={3}
            />
            <LanguageItem
              translation="Arabic"
              native="عربي"
              onClick={() => {
                props.changeLang("Arabic");
              }}
            />
            <LanguageItem
              translation="English"
              native="English"
              onClick={() => {
                props.changeLang("English");
              }}
            />
            <LanguageItem
              translation="Spanish"
              native="Español"
              onClick={() => {
                props.changeLang("Spanish");
              }}
            />
          </Box>
          <Box>
            <H4
              children={
                props.windowLang === "en-us"
                  ? "Contact us"
                  : props.windowLang === "es-mx"
                  ? "Contáctenos"
                  : "إتصل بنا"
              }
              mb={3}
            />
            <Box>
              <Text fontSize={1}>
                {props.windowLang === "en-us"
                  ? "Phone"
                  : props.windowLang === "es-mx"
                  ? "Teléfono"
                  : "الهاتف"}{" "}
                <a href="tel:+1 (202) 737-7752" target="_blank">
                  {" "}
                  +1 (202) 737-7752
                </a>
              </Text>
              <Text fontSize={1}>
                {props.windowLang === "en-us"
                  ? "Email"
                  : props.windowLang === "es-mx"
                  ? "E-mail"
                  : "البريد الإلكتروني"}{" "}
                <a href="mailto:info@bankinformationcenter.org" target="_blank">
                  info@bankinformationcenter.org
                </a>
              </Text>
            </Box>
            <Box mt={3}>
              {props.windowLang && (
                <PrimaryButton
                  children={
                    props.windowLang === "en-us"
                      ? "Newsletter Sign-up"
                      : props.windowLang === "es-mx"
                      ? "Inscríbase al boletín"
                      : "الاشتراك في الرسائل الإخبارية"
                  }
                  is="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={"http://eepurl.com/g7_4oD"}
                />
              )}
            </Box>
            <SocialMedia />
          </Box>
          <Box>
            <H4
              children={
                props.windowLang === "en-us"
                  ? "Bank Information Center"
                  : props.windowLang === "es-mx"
                  ? "Centro de Información Bancaria"
                  : "البنك معلومات مركز"
              }
              mb={3}
            />
            <Box mb={3}>
              <Box mb={3}>
                <Text fontSize={1}>1023 15th St NW</Text>
                <Text fontSize={1}>Washington DC, 20005</Text>
              </Box>
              <Text fontSize={1}>
                <em>
                  {props.windowLang === "en-us"
                    ? "Except where otherwise noted, content on this site by BIC is licensed under a Creative Commons Attribution 3.0 Unported License."
                    : props.windowLang === "es-mx"
                    ? ""
                    : ""}
                </em>
              </Text>
            </Box>
          </Box>
          <Box>
            <H4
              children={
                props.windowLang === "en-us"
                  ? "Support Us"
                  : props.windowLang === "es-mx"
                  ? "Apóyenos"
                  : "ادعم مركز معلومات البنك"
              }
              mb={3}
            />
            <Box mb={3}>
              <Text fontSize={1} mb={3}>
                {props.windowLang === "en-us"
                  ? "The work we do is funded through generous donations of foundations and individuals."
                  : props.windowLang === "es-mx"
                  ? "El trabajo que hacemos se financia a través de donaciones generosas de fundaciones e individuos."
                  : "تدعم المؤسسات الخاصة والافراد عملنا في مركز معلومات البنك من خلال التبرعات والمنح."}
              </Text>
              <Text fontSize={1}>
                <em>
                  {props.windowLang === "en-us"
                    ? "The Bank Information Center is a 501(c)(3) organization. Gifts are tax deductible to the full extent allowable under the law."
                    : props.windowLang === "es-mx"
                    ? "El trabajo que hacemos se financia a través de donaciones generosas de fundaciones e individuos. BIC es una organización 501 (c) (3). Los obsequios son deducibles de impuestos en la medida permitida por la ley."
                    : "ان مركز معلومات البنك هو منظمة غير هادفة للربح معفاة من الضرائب وفقًا للبند 501 (C)(3)‎ التابع لمصلحة الضرائب الأمريكية. التبرعات الى المركز معفاة من الضرائب إلى أقصى حد مسموح به بموجب القانون."}
                </em>
              </Text>
            </Box>
            {props.windowLang && (
              <PrimaryButton
                children={
                  props.windowLang === "en-us"
                    ? "Learn How"
                    : props.windowLang === "es-mx"
                    ? "Aprender cómo"
                    : "تعلم كيف"
                }
                is={Link}
                to={"/" + props.windowLang + "/support"}
              />
            )}
          </Box>
        </ItemLayout>
      </Container>
    </Wrapper>
    <Wrapper bg="grey.0">
      <Container>
        <Flex
          flexDirection={["column", null, "row"]}
          alignItems={["flex-start", null, "center"]}
        >
          <Flex flex={1} mb={[4, null, 0]}>
            <svg
              width="160px"
              height="17.355px"
              viewBox="0 0 1180 128"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M597.025 99.3882H608.135V27.1059H597.025V99.3882ZM602.674 15.0588C607.005 15.0588 610.583 11.6706 610.583 7.52941C610.583 3.38824 607.005 0 602.674 0C598.343 0 594.765 3.38824 594.765 7.52941C594.765 11.6706 598.343 15.0588 602.674 15.0588ZM313.764 99.3882H301.713L275.35 3.01176H287.213L307.362 81.5059H308.303L328.828 3.01176H342.01L362.535 81.5059H363.476L383.625 3.01176H395.488L369.125 99.3882H357.074L335.796 22.5882H335.042L313.764 99.3882ZM462.611 63.6235C462.611 86.0235 449.429 100.894 429.846 100.894C410.262 100.894 397.081 86.0235 397.081 63.6235C397.081 41.0353 410.262 26.1647 429.846 26.1647C449.429 26.1647 462.611 41.0353 462.611 63.6235ZM408.191 63.6235C408.191 78.1176 414.97 90.9176 429.846 90.9176C444.722 90.9176 451.501 78.1176 451.501 63.6235C451.501 49.1294 444.722 36.1412 429.846 36.1412C414.97 36.1412 408.191 49.1294 408.191 63.6235ZM478.244 99.3882H489.354V53.6471C489.354 43.8588 497.074 36.7059 507.619 36.7059C510.585 36.7059 513.645 37.2706 514.398 37.4588V26.1647C513.127 26.0706 510.209 25.9765 508.561 25.9765C499.899 25.9765 492.367 30.8706 489.731 38.0235H488.977V27.1059H478.244V99.3882ZM537.352 99.3882H526.242V3.01176H537.352V59.2941H538.67L570.305 27.1059H584.051L553.499 57.9765L586.311 99.3882H572.188L545.12 65.1294L537.352 72.3294V99.3882ZM656.722 36.1412C646.082 36.1412 638.268 43.2941 638.268 55.9059V99.3882H627.158V27.1059H637.891V38.4H638.833C642.222 31.0588 649.001 26.1647 659.923 26.1647C674.563 26.1647 684.402 35.0118 684.402 53.4588V99.3882H673.292V54.2118C673.292 42.8706 667.031 36.1412 656.722 36.1412ZM732.565 128C749.889 128 763.824 120.094 763.824 101.459V27.1059H753.09V38.5882H751.96C751.806 38.35 751.642 38.0922 751.469 37.8183L751.466 37.8129C748.898 33.7557 744.094 26.1647 730.87 26.1647C712.605 26.1647 699.989 40.6588 699.989 62.8706C699.989 85.4588 713.17 98.2588 730.682 98.2588C744.264 98.2588 748.964 90.5991 751.481 86.4963C751.581 86.3328 751.678 86.175 751.772 86.0235H752.714V100.706C752.714 112.753 744.24 118.212 732.565 118.212C719.898 118.212 715.157 111.778 712.175 107.732L712.001 107.496L711.852 107.294L703.001 113.506C707.521 121.082 716.418 128 732.565 128ZM732.189 88.2823C718.254 88.2823 711.099 77.7412 711.099 62.6824C711.099 48 718.066 36.1412 732.189 36.1412C745.746 36.1412 752.902 47.0588 752.902 62.6824C752.902 78.6824 745.558 88.2823 732.189 88.2823ZM888.089 87.6235C880.745 96.0471 872.224 100.706 859.467 100.706C833.857 100.706 816.534 81.6941 816.534 51.2C816.534 20.7059 834.422 1.69411 859.09 1.69411C879.427 1.69411 895.621 14.4941 899.199 33.1294H887.147C883.287 20.4235 872.978 12.6118 859.09 12.6118C841.954 12.6118 827.832 26.1647 827.832 51.2C827.832 76.2353 841.578 89.7882 859.467 89.7882C876.226 89.7882 887.901 78.0706 888.466 61.5529H862.103V51.2H899.764V99.3882H889.972L888.56 87.6235H888.089ZM916.892 99.3882H928.002V53.6471C928.002 43.8588 935.722 36.7059 946.267 36.7059C949.233 36.7059 952.293 37.2706 953.046 37.4588V26.1647C951.775 26.0706 948.856 25.9765 947.209 25.9765C938.547 25.9765 931.015 30.8706 928.378 38.0235H927.625V27.1059H916.892V99.3882ZM1024.83 63.6235C1024.83 86.0235 1011.65 100.894 992.065 100.894C972.481 100.894 959.3 86.0235 959.3 63.6235C959.3 41.0353 972.481 26.1647 992.065 26.1647C1011.65 26.1647 1024.83 41.0353 1024.83 63.6235ZM970.41 63.6235C970.41 78.1176 977.189 90.9176 992.065 90.9176C1006.94 90.9176 1013.72 78.1176 1013.72 63.6235C1013.72 49.1294 1006.94 36.1412 992.065 36.1412C977.189 36.1412 970.41 49.1294 970.41 63.6235ZM1086.03 69.8353C1086.03 83.3882 1075.68 89.6 1067.39 89.6C1058.16 89.6 1051.57 82.8235 1051.57 72.2824V27.1059H1040.46V73.0353C1040.46 91.4823 1050.25 100.329 1063.81 100.329C1074.73 100.329 1081.89 94.4941 1085.28 87.1529H1086.03V99.3882H1097.14V27.1059H1086.03V69.8353ZM1127.28 126.494H1116.17V27.1059H1126.9V38.5882H1128.22C1128.39 38.3158 1128.58 38.0177 1128.78 37.6991C1131.34 33.6156 1136.02 26.1647 1149.12 26.1647C1167.38 26.1647 1180 40.6588 1180 63.4353C1180 86.4 1167.38 100.894 1149.31 100.894C1135.95 100.894 1131.07 92.9337 1128.53 88.7937L1128.51 88.7589L1128.34 88.4809L1128.22 88.2823H1127.28V126.494ZM1147.8 36.1412C1134.24 36.1412 1127.09 47.0588 1127.09 63.2471C1127.09 79.6235 1134.43 90.9176 1147.8 90.9176C1161.73 90.9176 1168.89 78.6824 1168.89 63.2471C1168.89 48 1161.92 36.1412 1147.8 36.1412ZM0 2.76078H64.6258V20.1537H41.4268V81.9953H64.6258V99.3882H0V81.9953H23.199V20.1537H0V2.76078ZM143.751 2.76078H81.887V99.3882H100.115V59.6329H142.922V42.24H100.115V20.1537H143.751V2.76078ZM225.638 2.76078H161.012V20.1537H184.211V81.9953H161.012V99.3882H225.638V81.9953H202.439V20.1537H225.638V2.76078Z"
                fill="white"
              />
            </svg>
          </Flex>
          <Flex flex={2} flexDirection="column" mb={[4, null, 0]}>
            <H4 color="white">
              {props.windowLang === "en-us"
                ? "Looking for the IFI Working Group?"
                : props.windowLang === "es-mx"
                ? "¿Busca el Grupo de Trabajo de las IFI?"
                : "هل تبحث عن مجموعة عمل IFI؟"}
            </H4>
            <Text mt={2} color="white">
              {props.windowLang === "en-us"
                ? "BIC co-chairs the IFI Working Group and administers their website."
                : props.windowLang === "es-mx"
                ? "BIC copreside el Grupo de Trabajo de IFI y administra su sitio web."
                : " رئاسة مجموعة عمل IFI ويدير م"}
            </Text>
          </Flex>
          <Flex flex={1} mb={[4, null, 0]}>
            <Box pl={[0, 0, 5]}>
              <a
                href="https://ifiworkinggroup.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ThemeProvider theme={invertTheme}>
                  <Button>
                    {props.windowLang === "en-us"
                      ? "Learn more"
                      : props.windowLang === "es-mx"
                      ? "Aprende más"
                      : "أعرف أكثر"}
                  </Button>
                </ThemeProvider>
              </a>
            </Box>
          </Flex>
        </Flex>
      </Container>
    </Wrapper>
  </React.Fragment>
);

export default Footer;



// WEBPACK FOOTER //
// ./src/components/Footer.js